import compact from 'lodash/compact'
import map from 'lodash/map'
import toLower from 'lodash/toLower'
import uniq from 'lodash/uniq'
import type { Chat } from '@ztechapps/common.contract'

/**
 * Summarizes a batch-reply selection for BatchReplyModal's banner: the lower-cased sender emails, the distinct ones,
 * the ones shared by more than one conversation, and how many conversations have no sender email — those are left out
 * of the address lists, because sending to them fails rather than skips.
 */
export const summarizeBatchRecipients = (
  selectedList: Pick<Chat, 'sender'>[]
): { addresses: string[]; distinctAddresses: string[]; duplicateAddresses: string[]; noSenderCount: number } => {
  // A sender-less chat has no address to compare — compact it out before counting distinct and
  // duplicate addresses, or the blanks collide with each other and read as a false duplicate.
  const addresses = compact(map(selectedList, (chat) => toLower(chat.sender?.profile?.email ?? '')))
  const distinctAddresses = uniq(addresses)
  const duplicateAddresses = distinctAddresses.filter((address) => addresses.filter((a) => a === address).length > 1)
  // BatchReplyToChats sends one email per conversation through EmailChatGateway.addChatMessage,
  // which throws MISSING_RECIPIENT for a chat with no resolvable address
  // (libs/chat/node/src/gateways/email/EmailChatGateway.ts:77-83) — runChatBatch catches that per
  // conversation and reports it back as a failure, it does not silently skip or send anyway
  // (libs/chat/node/src/actions/runChatBatch.ts:27-33). So the banner counts only the addressed
  // conversations as "will be sent" and names the rest, which will fail instead.
  const noSenderCount = selectedList.length - addresses.length

  return { addresses, distinctAddresses, duplicateAddresses, noSenderCount }
}
